import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { applySiteMeta } from "@/lib/siteMeta";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Globe, Image as ImageIcon, Loader2, Save, Upload, Trash2,
  Search as SearchIcon, Share2, Twitter,
} from "lucide-react";

const TEXT_FIELDS = [
  // [key, label, placeholder, type, maxLen]
  ["site_name", "Site name", "Iceberg Digital", "input", 120],
  ["page_title", "Page title (browser tab)", "Iceberg Digital — Marketing Reports", "input", 160],
  ["title_template", "Title template", "%s | Iceberg Digital", "input", 200,
    "Used per route — '%s' is replaced with the page name. Leave blank to use a static page title."],
  ["meta_description", "Meta description", "Marketing analytics platform delivering branded monthly reports across Google, Meta, LinkedIn and more.", "textarea", 320],
  ["meta_keywords", "Meta keywords", "marketing, analytics, reports, ads, seo", "input", 320],
  ["canonical_url", "Canonical URL", "https://reports.example.com", "input", 400],
  ["robots", "Robots", "noindex, nofollow", "input", 120,
    "Use 'noindex, nofollow' to hide from search engines; 'index, follow' to allow indexing."],
  ["theme_color", "Theme colour (browser chrome)", "#002FA7", "input", 20],
  ["language", "Language (BCP47)", "en-AU", "input", 10],
];

const OG_FIELDS = [
  ["og_title", "OG title", "Iceberg Digital", "input", 160],
  ["og_description", "OG description", "Marketing analytics across every channel.", "textarea", 320],
  ["og_image_url", "OG image URL", "https://example.com/og.png", "input", 600],
  ["og_url", "OG URL", "https://reports.example.com", "input", 400],
  ["og_type", "OG type", "website", "input", 40],
  ["og_site_name", "OG site name", "Iceberg Digital", "input", 120],
];

const TWITTER_FIELDS = [
  ["twitter_card", "Twitter card", "summary_large_image", "input", 40],
  ["twitter_site", "Twitter @handle", "@icebergdigital", "input", 40],
  ["twitter_title", "Twitter title", "Iceberg Digital", "input", 160],
  ["twitter_description", "Twitter description", "Marketing analytics across every channel.", "textarea", 320],
  ["twitter_image_url", "Twitter image URL", "https://example.com/twitter.png", "input", 600],
];

function FieldGroup({ icon: Icon, title, fields, values, onChange }) {
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-100">
          <Icon className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">{title}</h2>
        </div>
        <div className="space-y-4">
          {fields.map(([key, label, placeholder, type, maxLen, help]) => (
            <div key={key} className="space-y-1.5">
              <Label htmlFor={`sm-${key}`} className="text-xs uppercase tracking-[0.15em] text-zinc-700">
                {label}
              </Label>
              {type === "textarea" ? (
                <Textarea id={`sm-${key}`} className="rounded-sm font-mono text-[12px]"
                  placeholder={placeholder} maxLength={maxLen} rows={3}
                  value={values[key] || ""}
                  onChange={(e) => onChange(key, e.target.value)}
                  data-testid={`sitemeta-${key}`} />
              ) : (
                <Input id={`sm-${key}`} className="rounded-sm font-mono text-[12px]"
                  placeholder={placeholder} maxLength={maxLen}
                  value={values[key] || ""}
                  onChange={(e) => onChange(key, e.target.value)}
                  data-testid={`sitemeta-${key}`} />
              )}
              {help && <p className="text-[11px] text-zinc-500">{help}</p>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FaviconPanel({ meta, onUploaded, onDeleted }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 256 * 1024) {
      toast.error(`Favicon too large (${(file.size / 1024).toFixed(0)} KB). Max 256 KB.`);
      e.target.value = "";
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/site-meta/favicon", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Favicon uploaded (${(data.size_bytes / 1024).toFixed(1)} KB)`);
      onUploaded(data.favicon_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove the current favicon? The browser will fall back to the default Iceberg logo.")) return;
    try {
      await api.delete("/site-meta/favicon");
      toast.success("Favicon removed");
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const previewUrl = meta?.favicon_url
    ? (meta.favicon_url.startsWith("http") ? meta.favicon_url : `${process.env.REACT_APP_BACKEND_URL || ""}${meta.favicon_url}`)
    : null;

  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-100">
          <ImageIcon className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">Favicon</h2>
        </div>
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 border border-zinc-200 rounded-sm bg-zinc-50 flex items-center justify-center overflow-hidden"
            data-testid="sitemeta-favicon-preview">
            {previewUrl
              ? <img src={previewUrl} alt="favicon" className="h-full w-full object-contain" />
              : <span className="text-[10px] text-zinc-400 uppercase tracking-wider">no upload</span>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-zinc-700">
              Upload a square <code className="px-1 bg-zinc-100 rounded-sm">.png</code>, <code className="px-1 bg-zinc-100 rounded-sm">.ico</code> or <code className="px-1 bg-zinc-100 rounded-sm">.svg</code> file (max 256 KB). It appears in browser tabs, bookmarks and mobile home screens.
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">
              Stored in the database and served via <code>/api/site-meta/favicon</code> — no extra hosting required.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <input ref={inputRef} type="file" className="hidden"
                accept=".ico,.png,.svg,.jpg,.jpeg,.gif,.webp,image/*"
                onChange={onPick}
                data-testid="sitemeta-favicon-input" />
              <Button type="button" variant="outline" className="rounded-sm" size="sm"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid="sitemeta-favicon-upload">
                {uploading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  : <Upload className="h-3.5 w-3.5 mr-1" />}
                Upload favicon
              </Button>
              {meta?.favicon_url && (
                <Button type="button" variant="ghost" size="sm" className="rounded-sm text-rose-600 hover:bg-rose-50"
                  onClick={onDelete}
                  data-testid="sitemeta-favicon-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SiteMetaPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({});

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/site-meta");
      setMeta(data || {});
    } catch (e) {
      toast.error("Failed to load site meta");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  const onChange = (key, val) => setMeta((m) => ({ ...m, [key]: val }));

  const save = async () => {
    setSaving(true);
    // Send everything as a partial PUT (the backend stores any non-None field).
    const body = {};
    [...TEXT_FIELDS, ...OG_FIELDS, ...TWITTER_FIELDS].forEach(([k]) => {
      body[k] = (meta[k] ?? "").toString();
    });
    try {
      const { data } = await api.put("/site-meta", body);
      setMeta(data);
      // Apply to the live document head immediately — no page refresh needed.
      applySiteMeta(data);
      toast.success("Site meta saved — applied to this tab");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading site meta…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="sitemeta-page">
      <div className="flex items-start justify-between gap-4 pb-4 border-b border-zinc-200">
        <div>
          <div className="label-overline">Admin · Site Meta &amp; SEO</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            Site metadata
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Edit how this app appears in browser tabs, search engine results,
            and social previews. Saved values are applied to every page load
            (admin + client portal). Favicon overrides the bundled Iceberg logo.
          </p>
        </div>
        <Button type="button" className="rounded-sm shrink-0" onClick={save}
          disabled={saving} data-testid="sitemeta-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save changes
        </Button>
      </div>

      <FaviconPanel
        meta={meta}
        onUploaded={(url) => {
          setMeta((m) => ({ ...m, favicon_url: url }));
          applySiteMeta({ ...meta, favicon_url: url });
        }}
        onDeleted={() => {
          setMeta((m) => ({ ...m, favicon_url: "" }));
          // Strip the favicon back to default on the current tab too.
          const link = document.head.querySelector('link[rel="icon"][data-sm="1"]');
          if (link) link.setAttribute("href", "/iceberg-logo.png");
        }} />

      <FieldGroup icon={SearchIcon} title="SEO basics" fields={TEXT_FIELDS}
        values={meta} onChange={onChange} />
      <FieldGroup icon={Share2} title="OpenGraph (Facebook / LinkedIn / Slack)"
        fields={OG_FIELDS} values={meta} onChange={onChange} />
      <FieldGroup icon={Twitter} title="Twitter / X card" fields={TWITTER_FIELDS}
        values={meta} onChange={onChange} />

      <div className="flex justify-end pt-2 border-t border-zinc-200">
        <Button type="button" className="rounded-sm" onClick={save}
          disabled={saving} data-testid="sitemeta-save-bottom">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
